import type { ProductInformation, Swatches } from '@dealerportal/common';
import { toCicLocale } from '@dealerportal/i18n';
import { useLocaleSafe } from '@dealerportal/i18n/client';
import {
  type SpecBuilderOptions,
  buildProductInformation,
  extractItemGroups,
} from '@dealerportal/utilities';
import uniq from 'lodash/uniq';
import { useCallback, useEffect, useState } from 'react';
import { decodeItemCode } from '../../../../utilities/src/lib/utilities';
import { useCatalogContext } from '../Contexts/CatalogContext';
import { CiCFrontendAdapter } from '../Mappers/CiCFrontendAdapter';
import { fetchCiCFeatures } from '../Mappers/cicFeatures';
import { fetchCatalogConfig } from './useCiCSearch';
import {
  TAG_SAMPLES,
  TAG_VISUALIZABLE,
  fetchVariants,
  transformMerchandisingToTag,
} from './useCiCSearch';

type CiCItemLookupResult = {
  products: ProductInformation[];
  isLoading: boolean;
};

const adapter = new CiCFrontendAdapter();

const localizationsCache: Record<string, Record<string, string>> = {};

/**
 * CiC's `characteristic.value` -> label dictionary for `locale` (lowercase
 * `xx-yy`, as the CDN names the files), cached per locale for the session.
 */
async function fetchLocalizations(
  locale = 'en-us'
): Promise<Record<string, string>> {
  const cached = localizationsCache[locale];
  if (cached) return cached;
  const scriptUrl = process.env.NEXT_PUBLIC_CIC_CLIENT_SCRIPT_URL ?? '';
  if (!scriptUrl) return {};
  const baseUrl = new URL('../', scriptUrl).href;
  const manifest: Record<string, string> = await fetch(
    `${baseUrl}manifest.json`
  ).then((r) => r.json());
  const path = manifest[`localizations/localizations.${locale}.json`];
  if (!path) {
    localizationsCache[locale] = {};
    return {};
  }
  const result: Record<string, string> = await fetch(`${baseUrl}${path}`).then(
    (r) => r.json()
  );
  localizationsCache[locale] = result;
  return result;
}

let catalogConfigCache: SpecBuilderOptions['config'];

export const useCiCItemLookup = (itemCodes: string[]): CiCItemLookupResult => {
  const [products, setProducts] = useState<ProductInformation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const { loaded, catalogVersionId } = useCatalogContext();
  const locale = useLocaleSafe();

  const fetchItem = useCallback(
    async (catalogVersionId: string, itemCode: string) => {
      const res = await globalThis.CiCAPI.content.v2.getItemVariant({
        itemId: `${catalogVersionId}.${itemCode}`,
      });

      const item = res.result;

      if (item == null) return null;

      const features = await fetchCiCFeatures([{ itemId: item.id }], {
        onlyVisibleSelectable: true,
      });

      const normalizedItem = adapter.adaptItem(item);

      const [itemGroupsResult, localizations, config] = await Promise.all([
        globalThis.CiCAPI.content.v2.getCatalogVersionItemGroups(
          catalogVersionId
        ),
        fetchLocalizations(toCicLocale(locale).toLowerCase()),
        fetchCatalogConfig(),
      ]);
      const normalizedGroups = adapter.adaptGroups(
        itemGroupsResult.result ?? []
      );

      if (normalizedGroups.length === 0) {
        console.warn(
          `No item groups found for catalog version ID: ${catalogVersionId}`
        );
      }

      const { brandGroup, productGroup } = extractItemGroups(
        normalizedItem,
        normalizedGroups
      );

      let { result: variants = [] } =
        await globalThis.CiCAPI.content.v2.getItemVariantProposals(
          { itemId: item.id },
          { variesBy: 'style' }
        );

      if (variants.length === 0) {
        variants = await fetchVariants(catalogVersionId, [
          productGroup?.id ?? '',
        ]);
      } else {
        variants.unshift(item);
      }

      // Sort them based on sequenceOrder
      variants.sort(
        (a, b) => (a.sequenceOrder ?? -1) - (b.sequenceOrder ?? -1)
      );

      // HACK: temporarily use the merchandising context from variant
      const merchandising =
        variants.find((variant) => variant.id === normalizedItem.id)?.context
          ?.merchandising ?? [];
      normalizedItem.context = { merchandising };

      // Create swatches from variants
      const swatches: Swatches[] = variants.map((variant) => {
        const { slug: variantSlug, sku: variantSku } = decodeItemCode(
          variant.code
        );
        const merchandising = variant.context?.merchandising ?? [];
        const variantTags = merchandising.map((tag) =>
          transformMerchandisingToTag(tag)
        );
        const filteredTags = variantTags.filter(
          (t) => t !== TAG_SAMPLES && t !== TAG_VISUALIZABLE
        );

        return {
          colorName: variant.names?.main ?? '',
          imageURL: variant.images?.main?.uri ?? '',
          slug: variantSlug,
          sku: variantSku,
          UPID: variant.refCodes?.manufCode ?? '',
          hasVisualization: variantTags.includes(TAG_VISUALIZABLE),
          hasSample: variantTags.includes(TAG_SAMPLES),
          tags: uniq(filteredTags),
          unitPrice: Number(variant.refCodes?.others?.pricing ?? 0),
        };
      });

      const info = buildProductInformation(
        normalizedItem,
        {
          brandName: brandGroup?.name || '',
          productName: productGroup?.name ?? '',
          swatches,
          features,
        },
        { localizations, config }
      );
      return info;
    },
    [locale]
  );

  // Use a stable string key so the effect doesn't re-fire on every render
  // when the caller passes a freshly-created array with the same contents.
  const itemCodesKey = itemCodes.join(',');

  useEffect(() => {
    if (!loaded || !catalogVersionId) return;
    setIsLoading(true);
    Promise.all(
      itemCodesKey
        .split(',')
        .filter(Boolean)
        .map((itemCode) => fetchItem(catalogVersionId, itemCode))
    )
      .then((results) => {
        const validResults = results.filter(
          (result): result is ProductInformation => result !== null
        );
        setProducts(validResults);
      })
      .finally(() => setIsLoading(false));
  }, [loaded, catalogVersionId, itemCodesKey, fetchItem]);

  return {
    products,
    isLoading,
  };
};
